import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { formatInTimeZone } from 'date-fns-tz';
import type AbilitiesService from 'ember-can/services/abilities';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import ConditionalLinkTo from 'garaje/components/conditional-link-to';
import type VisitComposite from 'garaje/models/visit-composite';
import type CurrentLocationService from 'garaje/services/current-location';
import type MetricsService from 'garaje/services/metrics';
import {
  DATE_FNS_DATE_WITH_DAY_OF_WEEK,
  DATE_FNS_12_HOUR_TIME,
  formatDateTimeInSystemTimezone,
} from 'garaje/utils/date-fns-tz-utilities';

interface DatetimeCellSignature {
  Args: {
    row: VisitComposite;
  };
}

export default class DatetimeCell extends Component<DatetimeCellSignature> {
  @service declare abilities: AbilitiesService;
  @service declare currentLocation: CurrentLocationService;
  @service declare metrics: MetricsService;

  get locationDate(): string {
    return formatInTimeZone(this.args.row.visitTime, this.args.row.locationTimezone, DATE_FNS_DATE_WITH_DAY_OF_WEEK);
  }

  get locationTime(): string {
    return formatInTimeZone(this.args.row.visitTime, this.args.row.locationTimezone, DATE_FNS_12_HOUR_TIME);
  }

  get dateTimeInSystemTimezone(): string {
    return formatDateTimeInSystemTimezone(this.args.row.visitTime);
  }

  get isCurrentLocation(): boolean {
    return this.args.row.locationId === this.currentLocation.location?.id;
  }

  get visitRoute(): string {
    const { type, id } = this.args.row.visitResource;
    if (!id) {
      return 'visitors.profiles.visits.show';
    }

    switch (type) {
      case 'invite':
        return 'visitors.invites.show';
      case 'entry':
        return 'visitors.entry';
      default:
        return '';
    }
  }

  get visitResourceId(): string {
    return this.args.row.visitResource.id;
  }

  get shouldLinkToVisit(): boolean {
    const { type } = this.args.row.visitResource;
    if (type === 'entry' && this.abilities.cannot('view all entries')) {
      return false;
    }

    if (type === 'invite' && this.abilities.cannot('view all invites')) {
      return false;
    }

    return this.isCurrentLocation && this.visitRoute !== '';
  }

  @action
  trackDateTimeCellClick(): void {
    this.metrics.trackEvent('Visitors Profiles Visits Table Click Into Visit', {
      visit_id: this.args.row.id,
    });
  }

  <template>
    <ConditionalLinkTo
      @condition={{this.shouldLinkToVisit}}
      @route={{this.visitRoute}}
      @model={{this.visitResourceId}}
      {{on "click" this.trackDateTimeCellClick}}
    >
      <div class="flex group" data-test-date-cell>
        <div>
          <EmberTooltip @side="top-start" @text={{this.dateTimeInSystemTimezone}} />
          <div
            class="flex flex-col text-carbon-50
              {{if this.shouldLinkToVisit 'hover:cursor-pointer group-hover:underline group-hover:text-red-50'}}
              "
          >
            <span data-test-date>{{this.locationDate}}</span>
            <span data-test-time>{{this.locationTime}}</span>
          </div>
        </div>
      </div>
    </ConditionalLinkTo>
  </template>
}
